import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
// import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import HorizontalBar from './HorizontalBar';
import MainContentStyled from './MainContentStyled';
// import Customization from '../Customization';
import Loader from 'ui-component/Loader';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import useGlobalSocketEvents from 'hooks/useGlobalSocketEvents';
import MainInnerContentStyled from './MainInnerContentStyled';
import LeftAppSidebar from './LeftAppSidebar';
import RightAppSidebar from './RightAppSidebar';
import ChatFloatingCommandRailLayout from './ChatFloatingCommandRailLayout';
import { MAIN_HEADER_TOOLBAR_HEIGHT_PX } from './layout-zoom-constants';
import { useCollapseRightWhenChatNarrow } from './useCollapseRightWhenChatNarrow';
import { dispatch, useSelector } from 'store';
import { closeAllSidebars } from 'store/slices/dual-sidebar';
import { closeAppSidebar } from 'store/slices/app-sidebar';
import AuthSync from 'utils/auth/AuthSync';
import OnboardingChecklistWidget from 'ui-component/onboarding/OnboardingChecklistWidget';
import { ChatQuickActionsProvider } from 'contexts/ChatQuickActionsContext';
import { ChatSidebarLayoutProvider } from 'contexts/ChatSidebarLayoutContext';
import { ChatSidebarSystemOrderProvider } from 'hooks/useChatSidebarSystemOrder';
import { ChatSidebarTeamOrderProvider } from 'hooks/useChatSidebarTeamOrder';
import { GuidedTourProvider } from 'guided-tours';
import { TemplateWatcherProvider } from 'contexts/TemplateWatcherContext';
import { useTemplateWatcher } from 'hooks/useTemplateWatcher';
import QaFloatingProgress from 'features/QAEngine/QaFloatingProgress';
import AssistantWidget from 'features/Assistant/AssistantWidget';
import { gridSpacing } from 'store/constant';
import GlobalAppHelpFloatingWidget from './Header/GlobalAppHelpFloatingWidget';
import { isNative } from 'platform/native';
import NativeBootstrap from 'platform/NativeBootstrap';

// ==============================|| MAIN LAYOUT ||============================== //

export default function MainLayout() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const templateWatcherControls = useTemplateWatcher();

  //* global socket events handler
  useGlobalSocketEvents();

  const { borderRadius, container, miniDrawer, menuOrientation } = useConfig();
  const { menuMaster, menuMasterLoading } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;
  const dualLeftOpen = useSelector((s) => s.dualSidebar.left.open);
  const dualRightOpen = useSelector((s) => s.dualSidebar.right.open);
  const chatToRightPanelGutter = dualRightOpen;

  const prevPathnameForSidebarRef = useRef<string | undefined>(undefined);
  const [mainInnerEl, setMainInnerEl] = useState<HTMLElement | null>(null);
  useCollapseRightWhenChatNarrow(mainInnerEl);

  useEffect(() => {
    // handlerDrawerOpen(!miniDrawer);
    handlerDrawerOpen(false);
  }, [miniDrawer]);

  useEffect(() => {
    downMD && handlerDrawerOpen(false);
  }, [downMD]);

  // סגירת סיידברים רק במעבר אזור (לא בתוך /chats → /chats)
  useEffect(() => {
    const next = location.pathname;
    const prev = prevPathnameForSidebarRef.current;
    prevPathnameForSidebarRef.current = next;
    if (prev === undefined) {
      dispatch(closeAllSidebars());
      closeAppSidebar();
      return;
    }
    if (prev === next) return;
    const prevInChat = prev.startsWith('/chats');
    const nextInChat = next.startsWith('/chats');
    if (prevInChat && nextInChat) return;
    dispatch(closeAllSidebars());
    closeAppSidebar();
  }, [location.pathname]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  // horizontal menu-list bar : drawer
  const menu = useMemo(() => (isHorizontal ? <HorizontalBar /> : <Sidebar />), [isHorizontal]);

  if (menuMasterLoading) return <Loader />;

  return (
    <GuidedTourProvider>
    <TemplateWatcherProvider value={templateWatcherControls}>
    <ChatQuickActionsProvider>
    <ChatSidebarLayoutProvider>
    <ChatSidebarSystemOrderProvider>
    <ChatSidebarTeamOrderProvider>
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* header */}
      <AuthSync />
      <NativeBootstrap />
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: 'background.default',
          /**
           * תפריטי ההדר (Popper) מרונדרים עם disablePortal ולכן כלואים בהקשר הערימה של ה-AppBar.
           * סיידברים בתוך התוכן מגיעים ל-1100 ומאוחר יותר ב-DOM, ולכן היו מכסים אותם.
           * נשאר מתחת ל-Drawer/Modal (1200+).
           */
          zIndex: 1150,
        }}
      >
        <Toolbar
          sx={{
            p: isHorizontal ? 1.25 : 2,
            minHeight: MAIN_HEADER_TOOLBAR_HEIGHT_PX,
            boxSizing: 'border-box',
          }}
        >
          <Header />
        </Toolbar>
      </AppBar>

      {/* menu / drawer */}
      {menu}

      {/* main content */}
      <MainContentStyled {...{ borderRadius, menuOrientation, open: drawerOpen }}>
        <Container
          maxWidth={container ? 'lg' : false}
          sx={{
            ...(!container && { px: { xs: 0 } }),
            flex: 1,
            minHeight: 0,
            height: '100%',
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {/* breadcrumb */}
          {/* <Breadcrumbs sx={{ flexShrink: 0 }} /> */}
          <Box
            sx={{
              flex: '1 1 auto',
              overflow: 'hidden',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'stretch',
              alignContent: 'stretch',
              justifyContent: 'flex-start',
              height: '100%',
              width: '100%',
              /**
               * ללא `gap` קבוע בין כל העמודות — כשסיידבר צדדי סגור (רוחב 0) ה־gap יוצר ריווח ריק מול קצה המסך.
               * ריווח רק כשהמגירה הדואלית באותו צד באמת פתוחה.
               */
              gap: 0,
              '& > *:nth-of-type(1)': {
                marginInlineEnd: (t) => (dualLeftOpen ? t.spacing(gridSpacing) : 0),
              },
              '& > *:nth-of-type(2)': {
                marginInlineEnd: (t) => (chatToRightPanelGutter ? t.spacing(gridSpacing) : 0),
              },
              overflowX: 'auto',
              overflowY: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                alignSelf: 'stretch',
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              <LeftAppSidebar />
            </Box>
            <Box
              sx={{
                flex: '1 1 0%',
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
              }}
            >
              <MainInnerContentStyled ref={setMainInnerEl} {...{ borderRadius }}>
                <ChatFloatingCommandRailLayout />
              </MainInnerContentStyled>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                alignSelf: 'stretch',
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              <RightAppSidebar />
            </Box>
          </Box>
          {/* <Footer /> */}
        </Container>
      </MainContentStyled>
      {!isNative && (
        <>
          <OnboardingChecklistWidget />
          <QaFloatingProgress />
          <GlobalAppHelpFloatingWidget />
          <AssistantWidget />
        </>
      )}
      {/* <Customization /> */}
    </Box>
    </ChatSidebarTeamOrderProvider>
    </ChatSidebarSystemOrderProvider>
    </ChatSidebarLayoutProvider>
    </ChatQuickActionsProvider>
    </TemplateWatcherProvider>
    </GuidedTourProvider>
  );
}
